import { useNavigate } from "react-router-dom";
import { X, Droplets, Thermometer, Wind, Camera, Cpu } from "lucide-react";
import { getWidgetConfig } from "../widgets/widgetConfig";
import GaugeWidget from "../widgets/GaugeWidget";
import ThermometerWidget from "../widgets/ThermometerWidget";
import StateWidget from "../widgets/StateWidget";
import UptimeWidget from "../widgets/UptimeWidget";

const WIDGET_COMPONENTS = {
  gauge: GaugeWidget,
  thermometer: ThermometerWidget,
  state: StateWidget,
  uptime: UptimeWidget,
};

const typeIcons = {
  "Capteur Humidité": { icon: Droplets, color: "text-blue-500", bg: "bg-blue-100" },
  "Capteur Température": { icon: Thermometer, color: "text-orange-500", bg: "bg-orange-100" },
  "Capteur CO2": { icon: Wind, color: "text-green-500", bg: "bg-green-100" },
  "Capteur Pression": { icon: Cpu, color: "text-purple-500", bg: "bg-purple-100" },
  "Capteur Qualité Air": { icon: Wind, color: "text-cyan-500", bg: "bg-cyan-100" },
  "Caméra IP": { icon: Camera, color: "text-slate-500", bg: "bg-slate-100" },
};

export default function DevicePanel({ device, projet, onClose, onToggle }) {
  const navigate = useNavigate();
  if (!device) return null;

  const iconConfig = typeIcons[device.type] || { icon: Cpu, color: "text-slate-500", bg: "bg-slate-100" };
  const Icon = iconConfig.icon;
  const isActif = device.etat === "En ligne";

  // Widget config
  const wConfig = getWidgetConfig(device.type, device.categorie);
  const WidgetComponent = WIDGET_COMPONENTS[wConfig.component] || GaugeWidget;

  const widgetProps = (() => {
    const val = typeof device.derniereValeur === 'number'
      ? device.derniereValeur
      : parseFloat(device.derniereValeur) || 0;

    switch (wConfig.component) {
      case 'state':
        return { value: device.derniereValeur, label: device.type };
      case 'uptime':
        return { value: val };
      case 'thermometer':
        return { value: val, unit: device.unite || wConfig.unit, min: wConfig.min, max: wConfig.max };
      case 'gauge':
      default:
        return { value: val, unit: device.unite || wConfig.unit, min: wConfig.min, max: wConfig.max, color: wConfig.color, thresholds: wConfig.thresholds };
    }
  })();

  return (
    <div className="absolute top-0 right-0 h-full w-80 bg-white z-[1001] shadow-2xl overflow-y-auto border-l border-gray-100">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconConfig.bg}`}>
              <Icon size={18} className={iconConfig.color} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">{device.sn}</h3>
              <p className="text-xs text-slate-400">{device.type}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Statut */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${isActif ? "bg-green-500" : "bg-red-500"}`} />
            <span className={`text-sm font-medium ${isActif ? "text-green-700" : "text-red-700"}`}>
              {isActif ? "En ligne" : "Hors ligne"}
            </span>
          </div>
          {device.uptime != null && isActif && (
            <span className="text-xs text-slate-400">Uptime: {device.uptime}%</span>
          )}
        </div>

        {/* Widget temps réel */}
        <div className="bg-gray-50 rounded-2xl p-5 flex flex-col items-center">
          <p className="text-xs text-slate-400 mb-3">Valeur en temps réel</p>
          <div className="transform scale-125 mb-2">
            <WidgetComponent {...widgetProps} />
          </div>
          {device.derniereMaj && (
            <p className="text-xs text-slate-400 mt-2">
              Mis à jour : {new Date(device.derniereMaj).toLocaleString("fr-FR")}
            </p>
          )}
        </div>

        {/* Infos */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <div>
            <p className="text-xs text-slate-400">Projet</p>
            <p className="text-xs font-semibold text-slate-700 truncate">{projet?.nom || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Localisation</p>
            <p className="text-xs font-semibold text-slate-700">{projet?.adresse || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Client</p>
            <p className="text-xs font-semibold text-slate-700">{projet?.clientNom || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Coordonnées GPS</p>
            <p className="text-xs font-semibold text-slate-700">
              {projet?.lat?.toFixed(4)}, {projet?.lng?.toFixed(4)}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-slate-400">Revendeur</p>
            <p className="text-xs font-semibold text-slate-700">{projet?.revendeurNom || "—"}</p>
          </div>
        </div>

        {/* Activer / Désactiver pour actionneurs en ligne */}
        {device.categorie === "Actionneur" && isActif && onToggle && (
          <div className="flex gap-2 mt-4">
            {device.derniereValeur === 1 || device.derniereValeur === "1" ? (
              <button
                onClick={() => onToggle(device._id, device.sn, false)}
                className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600"
              >
                Désactiver
              </button>
            ) : (
              <button
                onClick={() => onToggle(device._id, device.sn, true)}
                className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600"
              >
                Activer
              </button>
            )}
          </div>
        )}

        {/* Boutons */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => navigate(`/evenements?projet=${projet?._id || ""}`)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50"
          >
            Voir les événements
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 bg-slate-800 text-white rounded-xl text-sm font-medium hover:bg-slate-900">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
